import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TraceEvent } from '../../../../core/models/chat.models';

@Component({
  selector: 'app-trace-viewer',
  imports: [CommonModule],
  templateUrl: './trace-viewer.component.html',
  styleUrls: ['./trace-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TraceViewerComponent implements OnInit, OnDestroy, OnChanges {
  @Input() traces: TraceEvent[] = [];
  @Input() isStreaming = false;

  isExpanded = false;
  thinkingTime = 0;

  // Pre-computed properties replacing getters
  shouldShowTrace = false;
  thinkingSteps: Array<{ message: string; idx: number }> = [];
  currentStepMessage = 'Thinking...';

  private startTime = Date.now(); // Reset when new streaming session starts
  private intervalId: any = null;

  ngOnInit(): void {
    // If already streaming on init, start the timer
    if (this.isStreaming) {
      this.startTime = Date.now();
      this.thinkingTime = 0;
      this.startInterval();
    }
  }

  ngOnDestroy(): void {
    this.clearInterval();
  }

  ngOnChanges(changes: SimpleChanges): void {
    let recomputeTraces = false;

    if (changes['traces']) {
      recomputeTraces = true;
    }

    // Only care about isStreaming changes
    if (changes['isStreaming']) {
      recomputeTraces = true;
      const wasStreaming = changes['isStreaming'].previousValue;
      const nowStreaming = changes['isStreaming'].currentValue;

      // Only reset if transitioning from false/undefined to true
      // This handles the case where component was created with isStreaming=false
      if (!wasStreaming && nowStreaming) {
        // Started streaming - reset timer and start interval
        this.startTime = Date.now();
        this.thinkingTime = 0;
        this.startInterval();
      } else if (wasStreaming && !nowStreaming) {
        // Stopped streaming - stop the interval, keep the time, and close timeline
        this.clearInterval();
        this.isExpanded = false; // Close timeline when answer appears
      }
    }

    if (recomputeTraces) {
      this.computeTraceProperties();
    }
  }

  private startInterval(): void {
    // Clear any existing interval first
    this.clearInterval();

    // Start new interval
    this.intervalId = setInterval(() => {
      this.thinkingTime = Math.floor((Date.now() - this.startTime) / 1000);
    }, 100);
  }

  private clearInterval(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  // TrackBy function to prevent flickering
  trackByIdx(index: number, item: { message: string; idx: number }): number {
    return item.idx;
  }

  private computeTraceProperties(): void {
    this.shouldShowTrace = this.isStreaming || (this.traces && this.traces.length > 0);

    if (!this.traces || this.traces.length === 0) {
      this.thinkingSteps = [];
      this.currentStepMessage = 'Thinking...';
      return;
    }

    this.thinkingSteps = this.traces
      .map((trace, idx) => ({ message: this.extractThinkingMessage(trace), idx }))
      .filter(step => step.message !== null) as Array<{ message: string; idx: number }>;

    if (this.thinkingSteps.length === 0) {
      this.currentStepMessage = 'Thinking...';
    } else {
      this.currentStepMessage = this.thinkingSteps[this.thinkingSteps.length - 1].message;
    }
  }

  private extractThinkingMessage(traceContent: any): string | null {
    if (!traceContent) return null;
 
    if (typeof traceContent === 'string') {
      return traceContent;
    }
 
    if (typeof traceContent === 'object') {
      return traceContent.content || traceContent.message || null;
    }
 
    return null;
  }
}
